import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { enablePushNotifications } from "../lib/push";
import {
  Phone as PhoneIcon,
  Sms as SmsIcon,
  InstallMobile as InstallIcon,
  IosShare as IosShareIcon,
} from "@mui/icons-material";

interface SMSLoginResponse {
  message: string;
  phone: string;
  code?: string; // Только для демо режима
  user_role?: string; // Роль пользователя
}

interface VerifyResponse {
  access_token: string;
  token_type: string;
  user_role: "admin" | "master" | "client";
}

export default function SMSLoginPage() {
  const API_BASE = process.env.REACT_APP_API_URL || "/api";
  const loginAs = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const v = params.get("as");
    return v === "admin" || v === "client" ? v : null;
  }, []);
  const [phone, setPhone] = useState("");
  const [fullName, setFullName] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(0);
  const [demoCode, setDemoCode] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [installPrompt, setInstallPrompt] = useState<any | null>(
    () => (typeof window !== "undefined" ? (window as any).__deferredInstallPrompt || null : null)
  );
  const [installHint, setInstallHint] = useState(false);
  const isStandalone =
    typeof window !== "undefined" &&
    ((window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      (window.navigator as any).standalone === true);
  const isIOS =
    typeof window !== "undefined" && /iphone|ipad|ipod/i.test(window.navigator.userAgent);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const codeRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleOtpChange = (i: number, raw: string) => {
    const digits = raw.replace(/\D/g, "");
    if (!digits) return;
    setError("");
    setCode((prev) => (prev.slice(0, i) + digits + prev.slice(i + 1)).replace(/\D/g, "").slice(0, 4));
    const nextIdx = Math.min(i + digits.length, 3);
    setTimeout(() => codeRefs.current[nextIdx]?.focus(), 0);
  };

  const handleOtpKeyDown = (i: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (code[i]) {
        setCode(code.slice(0, i));
        codeRefs.current[i]?.focus();
      } else if (i > 0) {
        setCode(code.slice(0, i - 1));
        codeRefs.current[i - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && i > 0) {
      codeRefs.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < 3) {
      codeRefs.current[i + 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 4);
    if (!text) return;
    e.preventDefault();
    setCode(text);
    setTimeout(() => codeRefs.current[Math.min(text.length, 3)]?.focus(), 0);
  };

  useEffect(() => {
    const pickUp = () => setInstallPrompt((window as any).__deferredInstallPrompt || null);
    const handler = (e: any) => {
      e.preventDefault();
      (window as any).__deferredInstallPrompt = e;
      setInstallPrompt(e);
    };
    const installedHandler = () => {
      (window as any).__deferredInstallPrompt = null;
      setInstallPrompt(null);
    };

    // Если событие уже было поймано глобально до монтирования — подхватываем
    pickUp();

    window.addEventListener("beforeinstallprompt", handler as any);
    window.addEventListener("pwa-install-available", pickUp);
    window.addEventListener("appinstalled", installedHandler);
    window.addEventListener("pwa-installed", installedHandler);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler as any);
      window.removeEventListener("pwa-install-available", pickUp);
      window.removeEventListener("appinstalled", installedHandler);
      window.removeEventListener("pwa-installed", installedHandler);
    };
  }, []);

  const handleInstall = async () => {
    const dp = installPrompt || (window as any).__deferredInstallPrompt;
    if (!dp) {
      setInstallHint(true);
      return;
    }
    try {
      await dp.prompt();
      await dp.userChoice;
    } finally {
      (window as any).__deferredInstallPrompt = null;
      setInstallPrompt(null);
    }
  };

  useEffect(() => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 11) return;
    if (fullName.trim()) return;

    const normalized = digits.startsWith("8") ? `7${digits.slice(1)}` : digits;
    const phonePlus = `+${normalized}`;
    const ac = new AbortController();

    const t = window.setTimeout(() => {
      void (async () => {
        try {
          let name: string | undefined;
          // 1) сначала ищем сотрудника (пользователя)
          const ur = await fetch(`${API_BASE}/users/by-phone/${encodeURIComponent(phonePlus)}`, {
            signal: ac.signal,
          });
          if (ur.ok) {
            const u = (await ur.json()) as { full_name?: string };
            name = u?.full_name || undefined;
          }
          // 2) если сотрудника нет — ищем клиента
          if (!name) {
            const cr = await fetch(`${API_BASE}/clients/by-phone/${encodeURIComponent(phonePlus)}`, {
              signal: ac.signal,
            });
            if (cr.ok) {
              const c = (await cr.json()) as { name?: string };
              name = c?.name || undefined;
            }
          }
          if (name && !fullName.trim()) {
            setFullName(String(name));
          }
        } catch {
          /* ignore */
        }
      })();
    }, 350);

    return () => {
      window.clearTimeout(t);
      ac.abort();
    };
  }, [phone, fullName]);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim()) {
      setError("Заполните пожалуйста Имя нам надо как то вас индефицировать.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE}/sms/send-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ phone, full_name: fullName.trim() || null }),
      });

      if (response.ok) {
        const data: SMSLoginResponse = await response.json();
        if (data.user_role) {
          setUserRole(data.user_role);
        }
        setDemoCode(data.code || null);
        setStep(1);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Ошибка отправки SMS");
      }
    } catch (err) {
      setError("Не удалось подключиться к серверу");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE}/sms/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ phone, code, as_role: loginAs }),
      });

      if (response.ok) {
        const data: VerifyResponse = await response.json();
        
        // Сохраняем токен и роль
        localStorage.setItem("token", data.access_token);
        localStorage.setItem("user_role", data.user_role);

        // Сразу предлагаем включить уведомления (запрос разрешения — в ответ на вход)
        // и привязываем push-подписку устройства к вошедшему пользователю.
        try {
          await enablePushNotifications();
        } catch {
          /* не блокируем вход, если уведомления недоступны */
        }

        // Перенаправляем в зависимости от роли
        switch (data.user_role) {
          case "admin":
            window.location.replace("/");
            break;
          case "master":
            window.location.replace("/");
            break;
          case "client":
            window.location.replace("/");
            break;
          default:
            window.location.replace("/");
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Ошибка верификации кода");
      }
    } catch (err) {
      setError("Не удалось подключиться к серверу");
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (value: string) => {
    // Нормализуем номер - только цифры и + в начале
    const digits = value.replace(/\D/g, "");
    if (!digits) return "";
    if (digits.length === 11 && (digits.startsWith("8") || digits.startsWith("7"))) {
      return `+7${digits.slice(1)}`;
    }
    return `+${digits}`;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 450,
          width: "100%",
          bgcolor: "background.paper",
          boxShadow: 6,
          borderRadius: "6px",
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box
              component="img"
              src={isDark ? "/logo-white-192.png" : "/logo-dark-192.png"}
              alt="Зеркало"
              sx={{
                width: 120,
                height: 120,
                objectFit: "contain",
                mb: 1,
              }}
            />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Студия красоты «Зеркало»
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Онлайн-запись и личный кабинет
            </Typography>
          </Box>

          {!isStandalone && (
            <Box sx={{ mb: 3 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<InstallIcon />}
                onClick={() => void handleInstall()}
                sx={{ py: 1.25, borderRadius: 2, fontWeight: 700 }}
              >
                Установить приложение
              </Button>
              {installHint && !installPrompt && (
                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
                  {isIOS ? (
                    <Typography variant="caption" color="text.secondary">
                      На iPhone (Safari): нажмите «Поделиться»{" "}
                      <IosShareIcon sx={{ fontSize: 14, verticalAlign: "middle" }} /> → «На экран
                      «Домой»».
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Откройте меню браузера (⋮) и выберите «Установить приложение» / «Добавить на
                      главный экран».
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {step === 0 && (
            <form onSubmit={handleSendCode}>
              <TextField
                fullWidth
                label="Номер телефона"
                placeholder=""
                variant="outlined"
                margin="normal"
                value={phone}
                onChange={(e) => setPhone(formatPhone(e.target.value))}
                disabled={loading}
                autoFocus
                inputProps={{ maxLength: 12 }}
              />

              <TextField
                fullWidth
                label="Имя"
                placeholder="Иван Иванов"
                variant="outlined"
                margin="normal"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={loading}
              />

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || phone.replace(/\D/g, "").length < 11 || !fullName.trim()}
              >
                {loading ? <CircularProgress size={24} /> : "Отправить код"}
              </Button>
            </form>
          )}

          {step === 1 && (
            <form onSubmit={handleVerifyCode}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Код отправлен на номер {phone}
              </Typography>

              {demoCode && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Тестовый режим — ваш код: <b>{demoCode}</b>
                </Alert>
              )}

              <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", my: 2.5 }}>
                {[0, 1, 2, 3].map((i) => (
                  <TextField
                    key={i}
                    inputRef={(el) => {
                      codeRefs.current[i] = el as HTMLInputElement | null;
                    }}
                    value={code[i] || ""}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    onPaste={handleOtpPaste}
                    disabled={loading}
                    autoFocus={i === 0}
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: 1,
                      "aria-label": `Цифра ${i + 1}`,
                      style: {
                        textAlign: "center",
                        fontSize: 30,
                        fontWeight: 700,
                        padding: "10px 0",
                      },
                    }}
                    sx={{
                      width: { xs: 58, sm: 64 },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        "& fieldset": { borderWidth: 2 },
                      },
                    }}
                  />
                ))}
              </Box>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || code.length !== 4}
              >
                {loading ? <CircularProgress size={24} /> : "Войти"}
              </Button>

              <Button
                fullWidth
                variant="text"
                onClick={() => setStep(0)}
                disabled={loading}
              >
                Изменить номер
              </Button>
            </form>
          )}

          
        </CardContent>
      </Card>
    </Box>
  );
}
